import { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { QueryObserver, type QueryClient } from "@tanstack/react-query";

// Release 1.5.0 offline read (phone mode). While the connection drops on the floor:
//   • the last 10 work orders opened, "My work" and the dispatch list stay in memory (the answers already on the phone
//     are simply not thrown away; nothing extra is written to storage — the tab's kept copy is perf-1-5's
//     PersistQueryGate, cleared on sign-out);
//   • nothing is written: every change request is refused on the phone before it leaves ("You're offline — nothing was
//     sent"), React Query never pauses a change to send it later, and submit buttons are switched off. A signature or an
//     approval is never queued.
// Sign-out (AuthContext clears the query cache) or another person signing in drops everything kept here.

export const RECENT_WORK_ORDERS_MAX = 10;
export const OFFLINE_PROBLEM_TYPE = "https://summit-aviation.com/problems/offline";
export const OFFLINE_WRITE_TITLE = "You're offline — nothing was sent.";
export const OFFLINE_WRITE_DETAIL = "Changes, signatures and approvals are switched off until the connection is back. Nothing was saved or queued.";

/** The work-order key in a /work-orders/:key path (as the page reads it), or null. */
export function workOrderKeyFromPath(pathname: string): string | null {
  const m = /^\/work-orders\/([^/?#]+)\/?$/.exec(pathname);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]!);
  } catch {
    return null;
  }
}

/** Most recent first, no repeats, at most RECENT_WORK_ORDERS_MAX. */
export function rememberWorkOrder(recent: readonly string[], key: string): string[] {
  return [key, ...recent.filter((k) => k !== key)].slice(0, RECENT_WORK_ORDERS_MAX);
}

/** The query keys the keeper holds on to (work-order detail, My work, the dispatch list). */
export function keptQueryKeys(recent: readonly string[], include: { myWork: boolean; dispatch: boolean }): (readonly unknown[])[] {
  const keys: (readonly unknown[])[] = recent.map((k) => ["operations", "work-order-detail", k] as const);
  if (include.myWork) keys.push(MY_WORK_KEY);
  if (include.dispatch) keys.push(DISPATCH_KEY);
  return keys;
}

/** What MyAssignedWorkPage asks for. */
export const MY_WORK_KEY = ["me", "work"] as const;
/** What DispatchPage asks for first (the whole board; the person's shop is filtered on the page). */
export const DISPATCH_KEY: readonly unknown[] = ["shopfloor", "dispatch", "", "", "CriticalRatio"];

/**
 * Holds a silent, never-fetching observer on each kept query, so the answers already on the phone are not garbage
 * collected while the person walks the floor. It never fetches and never refetches anything itself.
 */
export class OfflineKeeper {
  private readonly observers = new Map<string, () => void>();

  constructor(private readonly queryClient: QueryClient) {}

  sync(keys: readonly (readonly unknown[])[]): void {
    const wanted = new Map(keys.map((k) => [JSON.stringify(k), k]));
    for (const [id, unsubscribe] of this.observers) {
      if (!wanted.has(id)) {
        unsubscribe();
        this.observers.delete(id);
      }
    }
    for (const [id, key] of wanted) {
      if (this.observers.has(id)) continue;
      const observer = new QueryObserver(this.queryClient, { queryKey: key, enabled: false });
      this.observers.set(id, observer.subscribe(() => undefined));
    }
  }

  get size(): number {
    return this.observers.size;
  }

  dispose(): void {
    for (const unsubscribe of this.observers.values()) unsubscribe();
    this.observers.clear();
  }
}

export function isWriteMethod(method: string | undefined): boolean {
  return !["get", "head", "options"].includes((method ?? "get").toLowerCase());
}

/**
 * Refuses every change request on the phone while it is offline: the request never leaves, and the page gets an
 * ordinary API error with the offline words. Returns the uninstall function.
 */
export function installOfflineWriteGuard(client: AxiosInstance, online: () => boolean): () => void {
  const id = client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (online() || !isWriteMethod(config.method)) return config;
    config.adapter = async (cfg: InternalAxiosRequestConfig) => {
      throw new AxiosError(OFFLINE_WRITE_TITLE, "ERR_OFFLINE", cfg, null, {
        status: 503,
        statusText: "Offline",
        headers: {},
        config: cfg,
        data: { type: OFFLINE_PROBLEM_TYPE, title: OFFLINE_WRITE_TITLE, detail: OFFLINE_WRITE_DETAIL, status: 503 }
      });
    };
    return config;
  });
  return () => client.interceptors.request.eject(id);
}

/** Changes are never paused to be sent later: a change made offline fails at once (the guard above refuses it). */
export function neverQueueMutations(queryClient: QueryClient): void {
  const defaults = queryClient.getDefaultOptions();
  queryClient.setDefaultOptions({ ...defaults, mutations: { ...defaults.mutations, networkMode: "always" } });
}

const DISABLED_MARK = "data-offline-disabled";
export const OFFLINE_BUTTON_TITLE = "You're offline — this needs a connection. Nothing is saved or queued.";

/** Submit buttons and anything marked data-write-action inside `root`, switched off with the reason (and back on). */
export function setWriteControlsDisabled(root: ParentNode, disabled: boolean): void {
  if (disabled) {
    root.querySelectorAll<HTMLElement>(
      'button[type="submit"]:not([data-offline-read]), form button:not([type]):not([data-offline-read]), input[type="submit"]:not([data-offline-read]), [data-write-action]'
    ).forEach((el) => {
      const control = el as HTMLElement & { disabled?: boolean };
      if (el.hasAttribute(DISABLED_MARK) || control.disabled === true) return;
      el.setAttribute(DISABLED_MARK, el.getAttribute("title") ?? "");
      el.setAttribute("title", OFFLINE_BUTTON_TITLE);
      if (typeof control.disabled === "boolean") control.disabled = true;
      else el.setAttribute("aria-disabled", "true");
    });
    return;
  }
  root.querySelectorAll<HTMLElement>(`[${DISABLED_MARK}]`).forEach((el) => {
    const control = el as HTMLElement & { disabled?: boolean };
    const title = el.getAttribute(DISABLED_MARK);
    if (title) el.setAttribute("title", title);
    else el.removeAttribute("title");
    el.removeAttribute(DISABLED_MARK);
    if (typeof control.disabled === "boolean") control.disabled = false;
    else el.removeAttribute("aria-disabled");
  });
}
